import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Button, Col, Container, Row } from 'react-bootstrap'

import './Library.scss'
import './Category.scss'

import {
  KEY_AFFIRMATION_ID,
  KEY_CATEGORY,
  ROUTE_ACTIVITIES_AFFIRMATIONS,
  ROUTE_ACTIVITIES_BREATHING_ALL,
  ROUTE_ACTIVITIES_COPINGSKILLS_ALL,
  ROUTE_ACTIVITIES_JOURNALING_ALL,
  ROUTE_ACTIVITIES_MEDITATION_ALL,
  ROUTE_ARTICLES_ALL,
  ROUTE_CATEGORY,
  ROUTE_DAILY_REMBLE,
  ROUTE_LIBRARY
} from 'components/common/helpers/Paths'
import Loading from 'templates/Loading'

import { ReactComponent as IconSeeAllArrow } from '../../../assets/images/see-all-arrow.svg'
import {
  getDailyRembleVideos,
  getWatchedDailyRembleVideos
} from 'components/common/api/DailyRemble'
import DailyRembleScrollingCards from 'components/daily-remble/DailyRembleScrollingCards'
import { getAuthenticatedUser } from 'components/auth'
import ScrollingCardsSkeleton from 'components/common/skeletons/ScrollingCardsSkeleton'
import ScrollingCards from 'components/common/navigation/ScrollingCards'
import { getPracticesByCategory } from 'components/common/api/Practices'
import ScrollingCardsPracticeList from 'components/common/navigation/ScrollingCardsPracticeList'
import ScrollingCardsPractices from 'components/common/navigation/ScrollingCardsPractices'
import {
  KEY_PRACTICE_JOURNALING_WP,
  buildRequestURL
} from 'components/common/api'
import { getCategoryById, getWpCategories } from 'components/common/api/Courses'
import CourseContext from 'components/common/contexts/CourseContext'
import { Link } from 'react-router-dom/cjs/react-router-dom'
import ArticlesList from 'components/articles/ArticlesList'
import {
  AFFIRMATION_ACTIVITY,
  BREATHING_ACTIVITY,
  CATEGORIES_WHITE_LIST,
  COPING_ACTIVITY,
  JOURNALING_ACTIVITY,
  MEDITATION_ACTIVITY
} from 'components/common/api/constants'
import { navigateTo } from 'components/common/navigation'

const sortFn = () => Math.random() - 0.5

const getPracticeByKey = (practices, key) =>
  practices
    .filter(p => p.post_type && p.post_type === key)
    .sort(sortFn)
    .slice(0, 9)

export default function Category({
  cache,
  setReturnPath,
  setHeaderTitle,
  location
}) {
  const { categoryId } = useParams()
  const { courses } = useContext(CourseContext)
  const initialCategory = location?.state?.category
  const history = useHistory()

  const [isLoading, setIsLoading] = useState(true)
  const [loadingContent, setLoadingContent] = useState(true)
  const [category, setCategory] = useState(
    initialCategory ? initialCategory : undefined
  )
  const [categories, setCategories] = useState([])
  const [activeSubcategory, setActiveSubcategory] = useState()
  const [categoryCourses, setCategoryCourses] = useState([])
  const [newDailyRembleVideos, setNewDailyRembleVideos] = useState([])
  const [dailyRembleVideos, setDailyRembleVideos] = useState([])
  const [practicesMeditation, setPracticesMeditation] = useState([])
  const [practicesJournaling, setPracticesJournaling] = useState([])
  const [practicesBreathwork, setPracticesBreathwork] = useState([])
  const [practicesCopyingSkills, setPracticesCopyingSkills] = useState([])
  const [affirmation, setAffirmation] = useState()

  const getCategoryAsync = useCallback(async () => {
    if (!categoryId) return

    const categoryResult = await cache.tryGetAsyncResult(
      `category-${categoryId}`,
      getCategoryById,
      this,
      categoryId
    )

    setCategory(categoryResult)

    let categoriesCourses = await cache.tryGetAsyncResult(
      `subcategories-${categoryResult.id}`,
      getWpCategories,
      this,
      categoryResult.id
    )
    if (categoriesCourses.length === 0) return

    const subCategoriesWhiteList = CATEGORIES_WHITE_LIST[
      categoryResult.name
    ].map(c => c.toLowerCase())
    categoriesCourses = categoriesCourses.filter(
      c => subCategoriesWhiteList.indexOf(c.name.toLowerCase()) > -1
    )

    setCategories(categoriesCourses)
    setIsLoading(false)
  }, [cache, categoryId])

  useEffect(() => {
    if (category) setHeaderTitle(category.name)
  }, [categoryId, category, setHeaderTitle])

  const getAllPracticesByCategory = useCallback(async () => {
    if (!category) return

    const practices = await getPracticesByCategory(
      activeSubcategory ? activeSubcategory.id : category.id
    )

    setPracticesJournaling([])
    setPracticesBreathwork([])
    setPracticesMeditation([])
    setPracticesCopyingSkills([])
    setAffirmation(undefined)

    const randomAffirmation = getPracticeByKey(practices, AFFIRMATION_ACTIVITY)
    if (randomAffirmation && randomAffirmation.length > 0)
      setAffirmation(randomAffirmation[0])

    const journalingPractices = getPracticeByKey(practices, JOURNALING_ACTIVITY)
    if (journalingPractices && journalingPractices.length > 0)
      setPracticesJournaling(journalingPractices)

    const breathworkPractices = getPracticeByKey(practices, BREATHING_ACTIVITY)
    if (breathworkPractices && breathworkPractices.length > 0)
      setPracticesBreathwork(breathworkPractices)

    const meditationPractices = getPracticeByKey(practices, MEDITATION_ACTIVITY)
    if (meditationPractices && meditationPractices.length > 0)
      setPracticesMeditation(meditationPractices)

    const copingPractices = getPracticeByKey(practices, COPING_ACTIVITY)
    if (copingPractices && copingPractices.length > 0)
      setPracticesCopyingSkills(copingPractices)

    setLoadingContent(false)
  }, [category, activeSubcategory])

  useEffect(() => {
    getCategoryAsync()
    getAllPracticesByCategory()
  }, [
    categoryId,
    getCategoryAsync,
    getAllPracticesByCategory,
    category,
    activeSubcategory
  ])

  useEffect(() => {
    setReturnPath(ROUTE_LIBRARY)
  }, [setReturnPath])

  const user = getAuthenticatedUser()
  const returnPath = ROUTE_CATEGORY.replace(`:${KEY_CATEGORY}`, categoryId)
  const routeArticlesViewAll = buildRequestURL(ROUTE_ARTICLES_ALL, {
    returnPath
  })

  /**
   * Effect to get the daily remble videos
   */
  useEffect(() => {
    if (!category) return
    // Get Daily Remble Videos
    async function getDailyRembleVideosAsync() {
      // Check cache first
      const result = await cache.tryGetAsyncResult(
        ROUTE_DAILY_REMBLE,
        getDailyRembleVideos,
        this,
        { per_page: 100 }
      )
      // Get videos already watched by the user
      const watchedVideos = getWatchedDailyRembleVideos()

      // Filter out videos that have already been watched
      const filteredVideos =
        result && result.length
          ? result.filter(video => {
              return (
                !watchedVideos.includes(video.id) &&
                video.categories.some(cat =>
                  cat.id.toString() === activeSubcategory
                    ? activeSubcategory.id
                    : category.id
                )
              )
            })
          : []

      setNewDailyRembleVideos(filteredVideos)
      setDailyRembleVideos(result || [])
    }

    // Run it
    getDailyRembleVideosAsync()
  }, [cache, category, activeSubcategory])

  useEffect(() => {
    if (!categoryId) return
    if (!courses) return

    const filteredCategoryCourses =
      courses &&
      courses.filter(course =>
        course.ld_course_category.includes(
          activeSubcategory ? activeSubcategory.id : categoryId
        )
      )
    setCategoryCourses(filteredCategoryCourses)
  }, [categoryId, courses, activeSubcategory])

  const categoryName = useMemo(() => {
    if (!category) return ''

    return activeSubcategory ? activeSubcategory.name : category.name
  }, [category, activeSubcategory])

  return (
    <Loading isLoaded={isLoading}>
      {category && categories.length > 0 && (
        <Container className='category'>
          <Row className='mt-2'>
            <Col className='scroll-categories'>
              <div className='scrolling-articles-wrapper'>
                <ul className='scrolling-card-cards'>
                  {categories &&
                    categories.map((cat, index) => (
                      <li
                        key={index}
                        className={`card pointer subcategory ${
                          activeSubcategory && activeSubcategory.id === cat.id
                            ? 'active'
                            : ''
                        }`}
                        onClick={() => {
                          setLoadingContent(true)
                          if (
                            activeSubcategory &&
                            cat.id === activeSubcategory.id
                          ) {
                            setActiveSubcategory(undefined)
                            return
                          }
                          setActiveSubcategory(cat)
                        }}
                      >
                        {cat.name}
                      </li>
                    ))}
                </ul>
              </div>
            </Col>
          </Row>

          <Loading
            isLoaded={loadingContent}
            style={{ height: 'calc(100vh - 273px)' }}
          >
            {categoryCourses.length > 0 && (
              <>
                <Row>
                  <h3>Courses for {categoryName}</h3>
                </Row>
                <ScrollingCards
                  courses={categoryCourses}
                  returnPath={ROUTE_LIBRARY}
                />
              </>
            )}

            <Row>
              <Col>
                <h2 className='today-title'>Daily Remble</h2>
                {dailyRembleVideos.length > 0 ? (
                  <DailyRembleScrollingCards
                    dailyRembleVideos={newDailyRembleVideos}
                    emptyText={`Wow${
                      user?.display_name && ', ' + user.display_name
                    }! You've watched all of the Daily Remble videos! Come back tomorrow for more. `}
                    returnPath={returnPath}
                  />
                ) : (
                  <ScrollingCardsSkeleton className={'daily-remble-skeleton'} />
                )}
              </Col>
            </Row>

            {practicesCopyingSkills.length > 0 && (
              <>
                <Row>
                  <Col>
                    <h3>Coping skills for {categoryName}</h3>
                  </Col>
                </Row>
                <ScrollingCardsPracticeList
                  practices={practicesCopyingSkills}
                  baseUrl={ROUTE_ACTIVITIES_COPINGSKILLS_ALL}
                  returnPath={returnPath}
                />
              </>
            )}

            {affirmation && (
              <Row className='mt-4'>
                <Col className='quote'>
                  <div>{affirmation.post_title}</div>
                  <Button
                    variant='link'
                    onClick={_ => {
                      const uri = ROUTE_ACTIVITIES_AFFIRMATIONS.replace(
                        `:${KEY_AFFIRMATION_ID}`,
                        affirmation.ID
                      )
                      const url = buildRequestURL(uri, { returnPath })
                      navigateTo(url, history)
                    }}
                  >
                    <span>See all</span> <IconSeeAllArrow />
                  </Button>
                </Col>
              </Row>
            )}

            {practicesMeditation.length > 0 && (
              <>
                <Row className='mt-4'>
                  <Col>
                    <h3>Meditation for {categoryName}</h3>
                  </Col>
                </Row>
                <ScrollingCardsPracticeList
                  practices={practicesMeditation}
                  baseUrl={ROUTE_ACTIVITIES_MEDITATION_ALL}
                  returnPath={returnPath}
                />
              </>
            )}

            {practicesJournaling.length > 0 && (
              <>
                <Row className='mt-4'>
                  <Col>
                    <h3>Journaling for {categoryName}</h3>
                  </Col>
                </Row>
                <ScrollingCardsPractices
                  key={KEY_PRACTICE_JOURNALING_WP}
                  practices={practicesJournaling}
                  baseUrl={ROUTE_ACTIVITIES_JOURNALING_ALL}
                  returnPath={returnPath}
                />
              </>
            )}

            {practicesBreathwork.length > 0 && (
              <>
                <Row className='mt-4'>
                  <Col>
                    <h3>Breathwork for {categoryName}</h3>
                  </Col>
                </Row>
                <ScrollingCardsPracticeList
                  practices={practicesBreathwork}
                  baseUrl={ROUTE_ACTIVITIES_BREATHING_ALL}
                  returnPath={returnPath}
                />
              </>
            )}
          </Loading>

          {/* Get latest articles from WP */}
          <div id='js-tour-articles' className='my-5'>
            <div className='articles-header d-flex justify-content-between align-items-center'>
              <h2 className='today-title'>Latest Articles</h2>
              <Link
                to={routeArticlesViewAll}
                className='view-all py-2 px-4 rounded-5'
              >
                See all
              </Link>
            </div>
            <div className='articles-list'>
              <ArticlesList
                sort
                cache={cache}
                count={3}
                returnPath={returnPath}
              />
            </div>
          </div>
        </Container>
      )}
    </Loading>
  )
}
